import React, { useCallback, useEffect, useRef, useState } from "react";
import { MetadataTypeEnum } from "build-ts/enums";
import { SortingParams_SortField, sortingParams_SortFieldToJSON } from "build-ts/interface";
import { fetchMetadataHeaders, HeaderWithStats } from "services/MetadataServices";
import { useUser } from "context/UserContext";
import MemoryConfigurationPanelComponent from "modules/memory/components/MemoryConfigurationPanel";

const MemoryConfigurationPanel: React.FC = () => {
  const { defaultTimeZone } = useUser();
  const [analystHeaders, setAnalystHeaders] = useState<HeaderWithStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAnalystId, setSelectedAnalystId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState<SortingParams_SortField>(SortingParams_SortField.LAST_MODIFIED);
  const [sortAscending, setSortAscending] = useState(false);

  // Wait 400ms after the user stops typing before firing the API call.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm]);

  const loadAnalysts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchMetadataHeaders(
        MetadataTypeEnum.ANALYST,
        -1,
        1,
        sortingParams_SortFieldToJSON(sortBy),
        sortAscending,
        debouncedSearch.trim(),
        "",
        [],
        false
      );
      setAnalystHeaders((response?.headers ?? []) as HeaderWithStats[]);
    } finally {
      setIsLoading(false);
    }
  }, [sortBy, sortAscending, debouncedSearch]);

  useEffect(() => {
    loadAnalysts();
  }, [loadAnalysts]);

  const handleSortChange = useCallback((field: SortingParams_SortField, ascending: boolean) => {
    setSortBy(field);
    setSortAscending(ascending);
  }, []);

  return (
    <MemoryConfigurationPanelComponent
      analystHeaders={analystHeaders}
      isLoading={isLoading}
      selectedAnalystId={selectedAnalystId}
      searchTerm={searchTerm}
      sortBy={sortBy}
      sortAscending={sortAscending}
      defaultTimeZone={defaultTimeZone}
      onSelectAnalyst={setSelectedAnalystId}
      onSearchChange={setSearchTerm}
      onSortChange={handleSortChange}
    />
  );
};

export default MemoryConfigurationPanel;
